import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import Typography from '@mui/joy/Typography';
import Textarea from '@mui/joy/Textarea';
import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Table from '@mui/joy/Table';
import Divider from '@mui/joy/Divider';

import useIdentity from 'hooks/useIdentity';
import useTeam from 'hooks/useTeam';
import useUserIdentity from 'hooks/useUserIdentity';

import Success from 'components/Success';
import Error from 'components/Error';
import Lore from 'components/Lore';
import IdentityAvatar from 'components/IdentityAvatar';
import CopyChip from 'components/CopyChip';
import AssistantOnly from 'components/AssistantOnly';

// import { money } from 'utils/misc';
import LoadingSection from './LoadingSection';
import { TeamType } from 'stores/team_store';
import useTeamInvite from 'hooks/useTeamInvite';
import { TeamInviteType } from 'stores/team_invite_store';
import { DateOrTime } from './Date';
import LockedFeature from './LockedFeature';
import useRoles from 'hooks/useRoles';


const TeamInviteList = observer(({ team }: { team: TeamType }) => {
    const { fetchList } = useTeamInvite();
    const { currentIdentity } = useUserIdentity();
    const [fetched, setFetched] = useState(false);
    const [invites, setInvites] = useState([] as TeamInviteType[]);


    useEffect(() => {
        document.title = "Team";
    }, []);


    useEffect(() => {
        if (!team.uuid) {
            return;
        }
        if (!currentIdentity?.uuid) {
            return;
        }
        if (fetched) return;

        fetchList(currentIdentity.uuid)
            .then((items) => {
                console.log('invites', items);
                setInvites(items);
            })
            .catch((e) => {
                console.error('Error fetching invites', e);
            })
            .finally(() => {
                console.log("invites", invites);
                setFetched(true);
            });

    }, [currentIdentity?.uuid, team?.uuid]);
    const base_url = `${window.location.origin}/team/invite/`;
    return (
        <Stack my={3}>
            <Typography level="title-md">Existing Invites</Typography>

            <Table>

                <thead>
                    <tr>
                        <th>Email</th>
                        <th>Accepted</th>
                        <th>Created</th>
                    </tr>
                </thead>

                {invites.map((i) => {
                    return (
                        <tr>
                            <td>
                                <Typography>{i.email}</Typography>
                            </td>
                            <td>
                                {i.accepted && <IdentityAvatar size="inline" identityKey={i.accepted_identity} />}
                                {!i.accepted &&
                                    <CopyChip copyText={base_url + i.uuid}>Invite Link</CopyChip>

                                }
                            </td>
                            <td><DateOrTime ts={i.date_created} /></td>

                        </tr>
                    );
                })}


            </Table>
        </Stack>
    );
});

const TeamInvite = observer(({team}: {team: TeamType }) => {
    const { currentIdentity } = useUserIdentity();
    const { createByEmail } = useTeamInvite();
    const [sending, setSending] = useState(false);

    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');

    function sendInvite() {
        setSending(true);
        createByEmail(currentIdentity.uuid, team.uuid, email, message)
            .then(() => {
                setSending(false);
                Success('We sent the invite!');
            })
            .catch(() => {
                setSending(false);
                Error('Error sending invite');
            });
    }


    return (
        <Stack my={3} spacing={1}>
            <Typography level="title-md">Invite a Team Member</Typography>
            <Stack spacing={1}>
                <Input
                    sx={{ width: "300px" }}
                    disabled={sending}
                    placeholder="Enter their email address"
                    onChange={(e) => setEmail(e.target.value)}
            />
            <Textarea
                    sx={{ width: "300px" }}
                    disabled={sending}
                    placeholder="Message"
                    onChange={(e) => setMessage(e.target.value)} />
            <Stack direction="row">
                <Button
                    disabled={sending}
                                       onClick={() => sendInvite()}>Invite</Button>
                                       </Stack>
            </Stack>

            <Stack>
            <TeamInviteList team={team} />
            </Stack>
        </Stack>
    );
});

const TeamProfile = observer(({team}: {team: TeamType }) => {
    const { fetchListByTeam, identities } = useIdentity();
    const { currentIdentity } = useUserIdentity();
    useEffect(() => {
        currentIdentity?.uuid && fetchListByTeam(currentIdentity.uuid, team.uuid);
    }, [team.uuid, currentIdentity?.uuid]);
    return (
        <Stack my={3}>
            <Typography level="title-lg">{team.name}</Typography>
            <Typography level="title-md">Team Members</Typography>
            <Stack my={1} spacing={1}>
                {identities.filter((i) => i.team === team.uuid).map((i) => {
                    return <IdentityAvatar size="inline" identityKey={i.uuid} />
                })}
            </Stack>

            <LockedFeature copy="Invite Team Members">
                <Stack m={2}>
                    <TeamInvite team={team} />
                </Stack>
            </LockedFeature>
        </Stack>
    );
});



const TeamAdminContainer = observer(() => {
    const { createTeam, fetchMyTeam, getTeam, items } = useTeam();
    const { hasRole } = useRoles();

    const { currentIdentity } = useUserIdentity();
    const [fetched, setFetched] = useState(false);
    const [teamName, setTeamName] = useState('');
    const [team, setTeam] = useState<TeamType|null>(getTeam(currentIdentity?.team));

    useEffect(() => {
        if(!fetched && currentIdentity?.uuid) {
            console.log('fetchMyTeam', currentIdentity?.uuid);
            fetchMyTeam(currentIdentity.uuid).then((m) => {
                console.log('fetchmyteam', m);
            });
            setFetched(true);
        }
    }, [currentIdentity?.uuid]);

    useEffect(() => {
        if(!team) {
            setTeam(getTeam(currentIdentity?.team));
        }
    }, [items.length]);


  useEffect(() => {
        document.title = "Team Admin";
    }, []);

    function saveTeam() {
        createTeam(currentIdentity.uuid, teamName)
            .then((t) => {
                if (t) {
                    setTeam(t);
                    Success('Team Created');
                }
                else {
                    Error("Unable to create team");
                }
            })
            .catch(() => {
                Error("Erorr creating team");
            });

    }


    if (!hasRole('manager'))
        return null;

    return (
        <AssistantOnly>
        <Stack mt={2}>

            {team && <TeamProfile team={team} />}
            {!fetched && <LoadingSection />}
            {fetched && !team &&
                <Stack my={2} spacing={1}>

                    <Typography level="title-lg">Create Team</Typography>
                    <Input placeholder="Team Name"
                        onChange={(e) => setTeamName(e.target.value)}
                    />
                    <Button onClick={() => saveTeam()}>Create Team</Button>
                </Stack>
            }

            <Divider />
            <Lore slug="feature/business/team" />
            </Stack>
        </AssistantOnly>
    );
});
export default TeamAdminContainer;
